// HoloSplashDual.jsx
import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { keyframes } from '@mui/system';

/* ---------------- Animations (공용) ---------------- */
const spinSlow = keyframes`
  from { transform: rotate(0deg) }
  to   { transform: rotate(360deg) }
`;
const spinFast = keyframes`
  from { transform: rotate(0deg) }
  to   { transform: rotate(360deg) }
`;
const sweep = keyframes`
  from { transform: rotate(0deg) }
  to   { transform: rotate(360deg) }
`;
const floatY = keyframes`
  0%,100% { transform: translateY(0) }
  50%     { transform: translateY(-8px) }
`;
const pulse = keyframes`
  0%,100% { opacity:.55 }
  50%     { opacity:1 }
`;
const rise = keyframes`
  from { transform: translateY(0);   opacity: .9 }
  to   { transform: translateY(-90px); opacity: 0 }
`;

/* ---------------- 메인 컴포넌트 ---------------- */
export default function HoloSplashDual({
  mode = 'dark',          // 'dark' | 'light'
  message = 'LOADING…',
  subMessage,             // 세부 메시지(선택)
  size = 240,             // HUD/홀로그램 기준 사이즈
  progress = undefined,   // 0~1 (선택: 진행률 원형 게이지)
  fullScreen = true,      // true면 화면 전체 오버레이
  logoSrc = undefined,    // 중앙에 로고/아이콘 넣고 싶을 때
}) {
  const isDark = mode === 'dark';

  // 색상 팔레트
  const C = useMemo(() => (
    isDark
      ? { bg: 'radial-gradient(ellipse at center, #0a0a1a 30%, #000 80%)',
          grid: '#00BFFF', pri: '#00BFFF', acc: '#00FFFF', text: '#9BE7FF' }
      : { bg: 'linear-gradient(180deg, #F7F9FF 0%, #FFFFFF 65%)',
          grid: '#8B5CF6', pri: '#7C3AED', acc: '#A78BFA', text: '#3A2E6E' }
  ), [isDark]);

  // 진행률 원형 게이지 값
  const r = size * 0.36;
  const circumference = 2 * Math.PI * r;
  const dash = typeof progress === 'number'
    ? Math.max(0, Math.min(1, progress)) * circumference
    : undefined;

  /* ------------ 공통 컨테이너 ------------ */
  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: fullScreen ? 'fixed' : 'relative',
        inset: fullScreen ? 0 : 'auto',
        width: fullScreen ? '100vw' : size * 2.6,
        height: fullScreen ? '100vh' : size * 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: C.bg,
        overflow: 'hidden',
        zIndex: 9999,
        '@media (prefers-reduced-motion: reduce)': {
          '*': { animation: 'none !important' }
        }
      }}
    >
      {/* 가는 그리드 및 스캔라인 */}
      <svg
        aria-hidden
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: isDark ? .12 : .06, zIndex: 1 }}
      >
        <defs>
          <pattern id="gridPattern" width="32" height="32" patternUnits="userSpaceOnUse">
            <path d="M32 0H0V32" fill="none" stroke={C.grid} strokeWidth="0.5"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#gridPattern)" />
      </svg>
      <Box
        aria-hidden
        sx={{
          position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none',
          background: isDark
            ? 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,191,255,.05) 3px, rgba(0,191,255,.05) 4px)'
            : 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(124,58,237,.05) 3px, rgba(124,58,237,.05) 4px)'
        }}
      />

      {/* ---------------- DARK 모드: 전술 HUD + 패널 ---------------- */}
      {isDark && (
        <>
          {/* 좌/우 사이드 패널 */}
          <SidePanelDark side="left" size={size} C={C} />
          <SidePanelDark side="right" size={size} C={C} />

          {/* 중앙 HUD */}
          <Box sx={{ position:'relative', width:size*1.6, height:size*1.6, zIndex:5 }}>
            <svg width={size*1.6} height={size*1.6} viewBox="0 0 300 300">
              <defs>
                <filter id="glowDark"><feGaussianBlur stdDeviation="3.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
                <linearGradient id="sweepDark" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor={C.acc} stopOpacity="0"/> <stop offset="100%" stopColor={C.acc} stopOpacity="1"/>
                </linearGradient>
              </defs>

              <g filter="url(#glowDark)">
                {[140,128,112,96,80].map((R,i)=>(
                  <circle key={R} cx="150" cy="150" r={R} fill="none"
                          stroke={i%2?C.pri:C.acc}
                          strokeWidth={i?0.75:1.25}
                          opacity={0.18 + i*0.1}
                          strokeDasharray={i===1?'10,5':i===2?'5,5':'1,0'} />
                ))}
                {/* 점선 링(느린 회전) */}
                <g style={{ transformOrigin:'150px 150px', animation:`${spinSlow} 26s linear infinite` }}>
                  <circle cx="150" cy="150" r="120" fill="none" stroke={C.acc} strokeWidth="1" opacity="0.7" strokeDasharray="2 18"/>
                </g>
                {/* 반대방향 링(빠른 회전) */}
                <g style={{ transformOrigin:'150px 150px', animation:`${spinSlow} 16s linear infinite reverse` }}>
                  <circle cx="150" cy="150" r="92" fill="none" stroke={C.pri} strokeWidth="1" opacity="0.5" strokeDasharray="40 10"/>
                </g>

                {/* 스윕 라인 */}
                <g style={{ transformOrigin:'150px 150px', animation:`${sweep} 3s linear infinite` }}>
                  <line x1="150" y1="150" x2="150" y2="10" stroke="url(#sweepDark)" strokeWidth="2"/>
                </g>

                {/* 중앙 로고/삼각형 */}
                {logoSrc ? (
                  <image href={logoSrc} x="120" y="120" width="60" height="60" preserveAspectRatio="xMidYMid meet" />
                ) : (
                  <path d="M150 112 L176 156 L124 156 Z" fill="rgba(0,191,255,.12)" stroke={C.acc} strokeWidth="2"/>
                )}

                {/* 진행률 원형 게이지 (선택) */}
                {typeof dash === 'number' && (
                  <circle
                    cx="150" cy="150" r={r} fill="none" stroke={C.acc} strokeWidth="4"
                    strokeDasharray={`${dash} ${circumference - dash}`} strokeLinecap="round"
                    style={{ transformOrigin:'150px 150px', transform:'rotate(-90deg)' }}
                  />
                )}
              </g>
            </svg>
          </Box>
        </>
      )}

      {/* ---------------- LIGHT 모드: 랩 홀로그램 테이블 ---------------- */}
      {!isDark && (
        <>
          {/* 좌/우 얇은 데이터 카드 */}
          <LightDataCard title="BIO-SCAN" size={size} C={C} sx={{ left:'8%', top:'18%', animationDelay:'0.6s' }}/>
          <LightDataCard title="METRICS" size={size} C={C} sx={{ right:'8%', top:'28%' }}/>

          {/* 하단 홀로그램 테이블 */}
          <Box sx={{ position:'relative', width:size*2, height:size*.9, mb:2, zIndex:4, transform:'rotateX(58deg)' }}>
            <svg width={size*2} height={size*.9} viewBox="0 0 420 170">
              <defs>
                <filter id="glowLight"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
                <radialGradient id="holoBase" cx="50%" cy="50%" r="55%">
                  <stop offset="0%" stopColor="rgba(167,139,250,.45)"/><stop offset="100%" stopColor="rgba(167,139,250,0)"/>
                </radialGradient>
              </defs>

              <g filter="url(#glowLight)">
                <ellipse cx="210" cy="85" rx="190" ry="70" fill="url(#holoBase)"/>
                {[165,135,110,85].map((R,i)=>(
                  <g key={R} style={{ transformOrigin:'210px 85px', animation:`${spinFast} ${5+i*2}s linear infinite` }}>
                    <ellipse cx="210" cy="85" rx={R} ry={R*.38} fill="none"
                             stroke={C.acc} strokeWidth="1.4"
                             strokeDasharray={i%2===0?'6,6':'10,2'} opacity={.5+i*.12}/>
                  </g>
                ))}
                {/* 스캔 빔 */}
                <g style={{ transformOrigin:'210px 85px', animation:`${sweep} 2.8s linear infinite` }}>
                  <line x1="210" y1="85" x2="400" y2="85" stroke={C.acc} strokeWidth="2" opacity=".9"/>
                </g>
                {/* 펄스 링 */}
                {[0,1].map(i=>(
                  <ellipse key={i} cx="210" cy="85" rx="82" ry="34" fill="none" stroke={C.acc} strokeWidth="2">
                    <animate attributeName="rx" values="82;120" dur="2s" begin={`${i*.9}s`} repeatCount="indefinite"/>
                    <animate attributeName="ry" values="34;52" dur="2s" begin={`${i*.9}s`} repeatCount="indefinite"/>
                    <animate attributeName="opacity" values=".8;0" dur="2s" begin={`${i*.9}s`} repeatCount="indefinite"/>
                  </ellipse>
                ))}
                {/* 진행률 (선택) */}
                {typeof dash === 'number' && (
                  <ellipse cx="210" cy="85" rx={r*1.05} ry={(r*1.05)*.4} fill="none" stroke={C.pri} strokeWidth="3"
                           strokeDasharray={`${dash} ${circumference - dash}`} strokeLinecap="round"
                           style={{ transformOrigin:'210px 85px', transform:'rotate(-90deg)' }}/>
                )}
              </g>
            </svg>

            {/* 떠오르는 파티클 */}
            {[...Array(22)].map((_,i)=>(
              <Box key={i} sx={{
                position:'absolute', bottom:'48%', left:`${10+Math.random()*80}%`,
                width:3, height:3, borderRadius:'50%', background: C.acc, opacity:.65,
                animation: `${rise} ${2.8+Math.random()*2}s ease-in infinite`,
                animationDelay: `${Math.random()*2.5}s`
              }}/>
            ))}
          </Box>
        </>
      )}

      {/* ---------------- 텍스트 블록 ---------------- */}
      <Box sx={{
        position:'absolute', bottom: fullScreen ? '10%' : 16, zIndex:10,
        textAlign:'center'
      }}>
        <Typography sx={{
          color: C.text, fontSize: 18, letterSpacing: isDark? '4px':'0.08em',
          fontFamily: isDark ? 'Orbitron, ui-monospace, monospace' : 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
          fontWeight: isDark? 700 : 600,
          textTransform: isDark? 'uppercase':'none',
          textShadow: isDark? '0 0 10px rgba(0,191,255,.6)':'none',
          animation: `${pulse} 2s ease-in-out infinite`
        }}>
          {message}
        </Typography>
        {subMessage && (
          <Typography sx={{ color: C.text, opacity:.75, mt: .5, fontSize: 12 }}>
            {subMessage}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/* ---------------- 서브 컴포넌트: DARK 패널 ---------------- */
function SidePanelDark({ side = 'left', size, C }) {
  const w = size * 0.32;
  return (
    <Box sx={{
      position:'absolute', top:'50%', transform:'translateY(-50%)',
      [side]: '5%', width:w, height:size*1.7,
      background:'rgba(10,20,30,.84)', border:`1px solid ${C.pri}`,
      boxShadow:`0 0 16px ${C.pri}66`, backdropFilter:'blur(6px)', p:1.5,
      display:'flex', flexDirection:'column', gap:1.3, zIndex:6
    }}>
      <PanelSection title="SYSTEM STATUS" value="ALL_NOMINAL" C={C}/>
      <PanelMeter   title="LOAD CAPACITY" value="600%" fill={0.75} C={C}/>
      <PanelBars    title="ENERGY OUTPUT (MW)" bars={[65,85,70,90,75,80,95,70,60,88]} C={C}/>
      <Typography sx={{ color:C.text, opacity:.55, fontSize:10 }}>
        {side === 'left'
          ? `GRID:A-1.3 | SYNC:${Math.random().toFixed(4)}`
          : `LAST PACKET: ${new Date().getMilliseconds()}ms`}
      </Typography>
    </Box>
  );
}
function PanelSection({ title, value, C }) {
  return (
    <Box sx={{ borderBottom:`1px solid ${C.pri}33`, pb:1 }}>
      <Typography sx={{ color:C.pri, fontFamily:'ui-monospace, monospace', fontSize:12, opacity:.7 }}>{title}</Typography>
      {value && <Typography sx={{ color:C.acc, fontFamily:'Orbitron, ui-monospace, monospace', fontWeight:700, letterSpacing:1 }}>{value}</Typography>}
    </Box>
  );
}
function PanelMeter({ title, value, fill=0.7, C }) {
  return (
    <Box sx={{ borderBottom:`1px solid ${C.pri}33`, pb:1 }}>
      <Typography sx={{ color:C.pri, fontFamily:'ui-monospace, monospace', fontSize:12, opacity:.7 }}>{title}</Typography>
      <Typography sx={{ color:C.acc, fontFamily:'Orbitron, ui-monospace, monospace', fontWeight:700, letterSpacing:1, mb:.8 }}>{value}</Typography>
      <Box sx={{ height:4, background:`${C.pri}1a` }}>
        <Box sx={{ height:'100%', width:`${Math.round(fill*100)}%`, background:C.pri }} />
      </Box>
    </Box>
  );
}
function PanelBars({ title, bars=[], C }) {
  return (
    <Box sx={{ borderBottom:`1px solid ${C.pri}33`, pb:1, flex:1, display:'flex', flexDirection:'column' }}>
      <Typography sx={{ color:C.pri, fontFamily:'ui-monospace, monospace', fontSize:12, opacity:.7 }}>{title}</Typography>
      <Box sx={{ flex:1, display:'flex', alignItems:'flex-end', gap:.5, mt:1 }}>
        {bars.map((h,i)=>(
          <Box key={i} sx={{
            flex:1, height:`${h}%`,
            background:`linear-gradient(to top, ${C.pri}, ${C.acc})`,
            opacity:.72, animation:`${pulse} ${1.8 + i*0.2}s ease-in-out infinite`
          }}/>
        ))}
      </Box>
    </Box>
  );
}

/* ---------------- 서브 컴포넌트: LIGHT 카드 ---------------- */
function LightDataCard({ title, size, C, sx }) {
  const w = size * 0.42;
  return (
    <Box sx={{
      position:'absolute', width:w, height:size*.82, zIndex:7,
      background:'linear-gradient(135deg, rgba(124,58,237,.06), rgba(167,139,250,.12))',
      border:`1px solid ${C.acc}66`, borderRadius:2, boxShadow:`0 8px 32px ${C.acc}29`,
      backdropFilter:'blur(10px)', p:1.5, animation:`${floatY} 4s ease-in-out infinite`, ...sx
    }}>
      <Typography sx={{ color:C.pri, fontFamily:'ui-monospace, monospace', fontSize:12, opacity:.7 }}>{title}</Typography>

      {/* 간단한 라인/바 그래프 */}
      <svg width="100%" height="62" viewBox="0 0 100 60" style={{ marginTop: 6 }}>
        <path d="M6,50 Q 20,12 34,40 T 64,28 T 94,50"
              fill="none" stroke={C.acc} strokeWidth="2">
          <animate attributeName="stroke-dashoffset" values="100;0" dur="2s" repeatCount="indefinite" />
        </path>
      </svg>
      <Box sx={{ display:'flex', flexDirection:'column', gap:.6, mt:1 }}>
        {[82,60,88].map((w,i)=>(
          <Box key={i} sx={{ height:3, background:`${C.acc}1a` }}>
            <Box sx={{ height:'100%', width:`${w}%`, background:C.acc }}/>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

